import React, { useEffect, useMemo, useState } from "react";
import { Row, Col, Card, CardBody, CardTitle, Badge, Spinner, Progress } from "reactstrap";
import { Link } from "react-router-dom";
import { fetchMagazaNames, fetchIlanNames } from "../../helpers/lookupNames";

const BASE_URL = "https://harmanappapi.stechomeyazilim.info";

// OData $count yanıtı BOM + sayı döndürebilir; sadece rakamları al.
const parseCount = (txt) => {
  const n = parseInt(String(txt).replace(/[^0-9]/g, ""), 10);
  return Number.isFinite(n) ? n : 0;
};

// Bir ID alanına göre grupla, en yüksek N kaydı döndür.
const topBy = (rows, field, n = 5) => {
  const map = {};
  rows.forEach((r) => {
    const id = r[field];
    if (id === null || id === undefined) return;
    map[id] = (map[id] || 0) + 1;
  });
  return Object.entries(map)
    .map(([id, count]) => ({ id: Number(id), count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, n);
};

const EngagementStats = () => {
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const token = localStorage.getItem("token") || authUser?.Token;
  const headers = { Authorization: `Bearer ${token}` };

  const [counts, setCounts] = useState(null);
  const [topStores, setTopStores] = useState([]);
  const [topLiked, setTopLiked] = useState([]);
  const [topCart, setTopCart] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchCount = async (entity) => {
      try {
        const res = await fetch(`${BASE_URL}/${entity}/$count`, { headers });
        if (!res.ok) return 0;
        return parseCount(await res.text());
      } catch {
        return 0;
      }
    };

    const fetchRows = async (entity, select) => {
      try {
        const res = await fetch(
          `${BASE_URL}/${entity}?$select=${select}&$top=5000`,
          { headers }
        );
        if (!res.ok) return [];
        const json = await res.json();
        return json.value || [];
      } catch {
        return [];
      }
    };

    (async () => {
      setLoading(true);
      // Toplam sayaçlar
      const [
        takip, favori, begeni, sepet, arama, alarm,
      ] = await Promise.all([
        fetchCount("MAGAZA_TAKIPCILERI"),
        fetchCount("MAGAZA_FAVORILERI"),
        fetchCount("ILAN_BEGENILERI"),
        fetchCount("SEPET"),
        fetchCount("KAYITLI_ARAMALAR"),
        fetchCount("ILAN_FIYAT_ALARMLARI"),
      ]);
      if (cancelled) return;
      setCounts({ takip, favori, begeni, sepet, arama, alarm });

      // Top listeler için ham veriyi çek
      const [takipRows, begeniRows, sepetRows] = await Promise.all([
        fetchRows("MAGAZA_TAKIPCILERI", "MAGAZA_ID"),
        fetchRows("ILAN_BEGENILERI", "ILAN_ID"),
        fetchRows("SEPET", "ILAN_ID"),
      ]);
      if (cancelled) return;

      const topStoreAgg = topBy(takipRows, "MAGAZA_ID");
      const topLikedAgg = topBy(begeniRows, "ILAN_ID");
      const topCartAgg = topBy(sepetRows, "ILAN_ID");

      // İsimleri çöz
      const [storeNames, ilanNames] = await Promise.all([
        fetchMagazaNames(topStoreAgg.map((r) => r.id)),
        fetchIlanNames([...topLikedAgg, ...topCartAgg].map((r) => r.id)),
      ]);
      if (cancelled) return;

      setTopStores(topStoreAgg.map((r) => ({ ...r, name: storeNames[r.id] || `Mağaza #${r.id}` })));
      setTopLiked(topLikedAgg.map((r) => ({ ...r, name: ilanNames[r.id] || `İlan #${r.id}` })));
      setTopCart(topCartAgg.map((r) => ({ ...r, name: ilanNames[r.id] || `İlan #${r.id}` })));
      setLoading(false);
    })();

    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const cards = useMemo(() => [
    { title: "Mağaza Takipleri", value: counts?.takip, icon: "bx-user-check", color: "primary", link: "/magaza-takipcileri" },
    { title: "Mağaza Favorileri", value: counts?.favori, icon: "bx-heart", color: "danger", link: "/magaza-favorileri" },
    { title: "İlan Beğenileri", value: counts?.begeni, icon: "bx-like", color: "success", link: "/ilan-begenileri" },
    { title: "Sepetteki Ürünler", value: counts?.sepet, icon: "bx-cart-add", color: "warning", link: "/sepetler" },
    { title: "Kayıtlı Aramalar", value: counts?.arama, icon: "bx-search-alt", color: "info", link: "/kayitli-aramalar" },
    { title: "Fiyat Alarmları", value: counts?.alarm, icon: "bx-bell", color: "secondary", link: "/ilan-fiyat-alarmlari" },
  ], [counts]);

  const TopList = ({ title, icon, rows, link, linkText, color, emptyText }) => {
    const max = rows.length ? rows[0].count : 0;
    return (
      <Card className="h-100">
        <CardBody>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <CardTitle className="mb-0">
              <i className={`bx ${icon} text-${color} me-1`} />
              {title}
            </CardTitle>
            <Link to={link} className="text-primary font-size-13">
              {linkText} <i className="mdi mdi-arrow-right" />
            </Link>
          </div>
          {loading ? (
            <div className="text-center py-4"><Spinner size="sm" color={color} /></div>
          ) : rows.length === 0 ? (
            <div className="text-center text-muted py-4">
              <i className={`bx ${icon} display-4 opacity-25 d-block mb-2`} />
              {emptyText}
            </div>
          ) : (
            rows.map((r, i) => (
              <div key={r.id} className="mb-3">
                <div className="d-flex justify-content-between mb-1">
                  <span className="font-size-13 fw-medium text-truncate" style={{ maxWidth: "75%" }}>
                    <Badge color={color} className="me-1">{i + 1}</Badge>
                    {r.name}
                  </span>
                  <span className="text-muted font-size-12">{r.count}</span>
                </div>
                <Progress value={max ? Math.round((r.count / max) * 100) : 0} color={color} className="progress-sm" />
              </div>
            ))
          )}
        </CardBody>
      </Card>
    );
  };

  return (
    <>
      {/* ETKİLEŞİM SAYAÇLARI */}
      <Row className="mb-2">
        {cards.map((s, i) => (
          <Col key={i} xs={6} md={4} xl={2}>
            <Card className="mini-stats-wid mb-3">
              <CardBody className="py-3 px-3">
                <Link to={s.link} className="d-flex align-items-center text-reset text-decoration-none">
                  <div className="flex-grow-1 overflow-hidden">
                    <p className="text-muted fw-medium mb-1 font-size-12 text-truncate">{s.title}</p>
                    <h5 className="mb-0 font-size-16">
                      {counts === null ? <Spinner size="sm" color={s.color} /> : (s.value ?? 0)}
                    </h5>
                  </div>
                  <div className={`avatar-sm rounded-circle bg-${s.color} ms-2 flex-shrink-0`}>
                    <span className={`avatar-title rounded-circle bg-${s.color}`}>
                      <i className={`bx ${s.icon} font-size-18`} />
                    </span>
                  </div>
                </Link>
              </CardBody>
            </Card>
          </Col>
        ))}
      </Row>

      {/* TOP ETKİLEŞİM LİSTELERİ */}
      <Row>
        <Col xl={4}>
          <TopList
            title="En Çok Takip Edilen Mağazalar"
            icon="bx-user-check" color="primary"
            rows={topStores} link="/magaza-takipcileri" linkText="Tümü"
            emptyText="Henüz takip verisi yok"
          />
        </Col>
        <Col xl={4}>
          <TopList
            title="En Çok Beğenilen İlanlar"
            icon="bx-like" color="success"
            rows={topLiked} link="/ilan-begenileri" linkText="Tümü"
            emptyText="Henüz beğeni verisi yok"
          />
        </Col>
        <Col xl={4}>
          <TopList
            title="En Çok Sepete Eklenen İlanlar"
            icon="bx-cart-add" color="warning"
            rows={topCart} link="/sepetler" linkText="Tümü"
            emptyText="Henüz sepet verisi yok"
          />
        </Col>
      </Row>
    </>
  );
};

export default EngagementStats;
